import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Lock, Star, TrendingUp } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MembersPage = () => {
  const { user, login, loading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }

    if (user && !user.is_member) {
      toast.info('This content is for members only');
      return;
    }

    if (user && user.is_member) {
      const fetchPremiumContent = async () => {
        try {
          const response = await axios.get(`${API}/articles/premium`, { withCredentials: true });
          setArticles(response.data);
        } catch (error) {
          console.error('Error fetching premium articles:', error);
          toast.error('Failed to load premium content');
        } finally {
          setContentLoading(false);
        }
      };

      fetchPremiumContent();
    }
  }, [user, loading, navigate]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="loading">Loading...</div>;
  }

  if (!user) {
    return null;
  }

  if (!user.is_member) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4" data-testid="non-member-view">
        <div className="max-w-2xl text-center">
          <Lock className="h-20 w-20 text-[#007AFF] mx-auto mb-6" />
          <h1 className="text-4xl font-bold uppercase mb-4 heading-font text-[#001f3f]">Members Only</h1>
          <p className="text-lg text-slate-700 mb-6">
            This content is exclusively for SheLovesThee members. Join our community for free to access:
          </p>
          <div className="text-left max-w-md mx-auto mb-8 space-y-3">
            <div className="flex items-start space-x-3">
              <Star className="h-5 w-5 text-[#DC2626] mt-1" />
              <span>Exclusive articles and insider analysis</span>
            </div>
            <div className="flex items-start space-x-3">
              <Star className="h-5 w-5 text-[#DC2626] mt-1" />
              <span>Advanced predictions and breakdowns</span>
            </div>
            <div className="flex items-start space-x-3">
              <Star className="h-5 w-5 text-[#DC2626] mt-1" />
              <span>Early access to QB Club content</span>
            </div>
          </div>
          <Button 
            onClick={login}
            className="bg-[#007AFF] text-white px-8 py-3 rounded-full font-bold uppercase tracking-wider hover:scale-105 transition-transform"
            data-testid="become-member-button"
          >
            Become a Member (Free)
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="members-page">
      <div className="bg-gradient-to-r from-[#001f3f] to-[#007AFF] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center space-x-4 mb-4">
            <Star className="h-12 w-12 text-[#DC2626]" />
            <h1 className="text-5xl md:text-6xl font-bold tracking-tight uppercase heading-font" data-testid="members-title">
              Members Only
            </h1>
          </div>
          <p className="text-xl text-slate-200">Welcome back, {user.name}! Exclusive content just for you.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white p-6 shadow-lg" data-testid="member-stat-1">
            <TrendingUp className="h-10 w-10 text-[#007AFF] mb-3" />
            <h3 className="text-2xl font-bold uppercase heading-font text-[#001f3f] mb-2">Deep Dives</h3>
            <p className="text-slate-600">In-depth analysis you won't find anywhere else</p>
          </div>
          <div className="bg-white p-6 shadow-lg" data-testid="member-stat-2">
            <Star className="h-10 w-10 text-[#DC2626] mb-3" />
            <h3 className="text-2xl font-bold uppercase heading-font text-[#001f3f] mb-2">Insider Access</h3>
            <p className="text-slate-600">Get the inside scoop before anyone else</p>
          </div>
          <div className="bg-white p-6 shadow-lg" data-testid="member-stat-3">
            <Lock className="h-10 w-10 text-[#007AFF] mb-3" />
            <h3 className="text-2xl font-bold uppercase heading-font text-[#001f3f] mb-2">Premium Only</h3>
            <p className="text-slate-600">Content reserved for community members</p>
          </div>
        </div>

        <h2 className="text-4xl font-bold tracking-tight uppercase text-[#001f3f] mb-8 heading-font" data-testid="premium-articles-title">
          Premium Articles
        </h2>

        {contentLoading ? (
          <div className="text-center py-12" data-testid="content-loading">Loading premium content...</div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12 text-slate-600" data-testid="no-premium-content">
            Premium content coming soon! Check back later.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map(article => (
              <a
                key={article.article_id}
                href={`/news/${article.article_id}`}
                className="group bg-white border-2 border-[#DC2626] hover:border-[#007AFF] transition-all shadow-lg hover:shadow-xl overflow-hidden"
                data-testid={`premium-article-${article.article_id}`}
              >
                {article.image_url && (
                  <div className="h-48 overflow-hidden relative">
                    <img 
                      src={article.image_url} 
                      alt={article.title} 
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute top-2 right-2 bg-[#DC2626] text-[#001f3f] px-3 py-1 text-xs font-bold uppercase">
                      Premium
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <span className="text-xs font-bold uppercase tracking-wider text-[#007AFF] mb-2 inline-block">
                    {article.category}
                  </span>
                  <h3 className="text-xl font-bold uppercase mb-2 heading-font text-[#001f3f] line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-slate-600 line-clamp-2">{article.excerpt}</p>
                </div>
              </a>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MembersPage;